import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {ZoomableLightboxProps} from './zoomable-lightbox.types';
import styles from './zoomable-lightbox.module.scss';
import {Lightbox} from '@Components/lightbox/lightbox';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Swiper} from '@Components/swiper/swiper';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader/circular-progress-loader';

export function ZoomableLightbox({
  isOpen,
  onClose,
  imagesAttachmentData,
  currentImageIndex,
  onPreviousImage,
  onNextImage,
  headerContent,
  enableZoom = true,
  imageAlt = 'Image',
  updateWindowHistory = true,
  ...props
}: ZoomableLightboxProps): ReactElement | null {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({x: 0, y: 0});
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({x: 0, y: 0});
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [hasDragged, setHasDragged] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const pushedStateRef = useRef(false);
  const closedByPopRef = useRef(false);
  const currentImage = imagesAttachmentData[currentImageIndex];
  const hasPrevious = currentImageIndex > 0;
  const hasNext = currentImageIndex < imagesAttachmentData.length - 1;
  const hasMultipleImages = imagesAttachmentData.length > 1;

  useEffect(() => {
    if (!isOpen) {
      setZoom(1);
      setPosition({x: 0, y: 0});
    }
  }, [isOpen]);

  useEffect(() => {
    setZoom(1);
    setPosition({x: 0, y: 0});
    setIsImageLoading(true);
  }, [currentImageIndex]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!isOpen) {
        return;
      }

      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft' && hasPrevious) {
        onPreviousImage();
      } else if (event.key === 'ArrowRight' && hasNext) {
        onNextImage();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, hasPrevious, hasNext, onClose, onPreviousImage, onNextImage]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent): void => {
      if (!enableZoom || !isOpen) {
        return;
      }
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.max(1, Math.min(5, zoom + delta));
      setZoom(newZoom);
      if (newZoom === 1) {
        setPosition({x: 0, y: 0});
      }
    };

    const imageContainer = imageRef.current?.parentElement;
    if (isOpen && imageContainer) {
      imageContainer.addEventListener('wheel', handleWheel, {passive: false});
    }

    return (): void => {
      if (imageContainer) {
        imageContainer.removeEventListener('wheel', handleWheel);
      }
    };
  }, [isOpen, enableZoom, zoom]);

  useEffect(() => {
    if (!updateWindowHistory) {
      return;
    }

    const onPopState = (): void => {
      if (pushedStateRef.current) {
        pushedStateRef.current = false;
        closedByPopRef.current = true;
        onClose();
      }
    };

    if (isOpen && !pushedStateRef.current) {
      closedByPopRef.current = false;
      const state = {pmwZoomableLightbox: true, unique: Date.now()};
      window.history.pushState(state, document.title);
      pushedStateRef.current = true;
      window.addEventListener('popstate', onPopState);
    } else if (isOpen && pushedStateRef.current) {
      window.addEventListener('popstate', onPopState);
    }

    return (): void => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [isOpen, updateWindowHistory, onClose]);

  const handleCloseLightbox = (): void => {
    if (pushedStateRef.current && !closedByPopRef.current) {
      window.history.back();
      pushedStateRef.current = false;
    }
    onClose();
    setZoom(1);
    setPosition({x: 0, y: 0});
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (enableZoom && zoom > 1) {
      setIsDragging(true);
      setHasDragged(false);
      setDragStart({x: e.clientX - position.x, y: e.clientY - position.y});
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (isDragging && zoom > 1) {
      if (!hasDragged) {
        setHasDragged(true);
      }
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = (): void => {
    setIsDragging(false);
  };

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>): void => {
    e.stopPropagation();
    if (!enableZoom || hasDragged) {
      return;
    }
    if (zoom === 1) {
      setZoom(1.5);
    } else {
      setZoom(1);
      setPosition({x: 0, y: 0});
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      handleCloseLightbox();
    }
  };

  const handleContextMenu = (e: React.MouseEvent): void => {
    e.preventDefault();
  };

  const getPreviousImageIcon = (): ReactElement | null => {
    if (!hasMultipleImages) {
      return null;
    }

    return (
      <Icon
        icon="icon-caret-left"
        size={IconSize.SIZE_ICON_24}
        type={IconType.DARK_GHOST}
        shape={IconShape.CIRCLE}
        onClick={hasPrevious ? onPreviousImage : undefined}
        className={`_position-absolute ${styles.navigationIcon} ${styles.navigationIconLeft} ${!hasPrevious ? styles.navigationIconDisabled : ''}`}
        isDisabled={!hasPrevious}
      />
    );
  };

  const getNextImageIcon = (): ReactElement | null => {
    if (!hasMultipleImages) {
      return null;
    }

    return (
      <Icon
        icon="icon-caret-right"
        size={IconSize.SIZE_ICON_24}
        type={IconType.DARK_GHOST}
        shape={IconShape.CIRCLE}
        onClick={hasNext ? onNextImage : undefined}
        className={`_position-absolute ${styles.navigationIcon} ${styles.navigationIconRight} ${!hasNext ? styles.navigationIconDisabled : ''}`}
        isDisabled={!hasNext}
      />
    );
  };

  const getLoaderIfNeeded = (): ReactElement | null => {
    if (isImageLoading) {
      return <CircularProgressLoader classes="_margin-auto" size={LOADER_SIZE.LARGE} color={LOADER_COLOR.WHITE} />;
    }

    return null;
  };

  const getZoomableImage = (): ReactElement => {
    return (
      <div
        className={styles.zoomableImageContainer}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleBackgroundClick}
        onContextMenu={handleContextMenu}
      >
        {getLoaderIfNeeded()}
        <img
          ref={imageRef}
          src={currentImage.src}
          alt={currentImage.alt ?? imageAlt}
          className={styles.zoomableImage}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            cursor: enableZoom && zoom > 1 ? (isDragging ? 'grabbing' : 'zoom-out') : enableZoom ? 'zoom-in' : 'default',
            display: isImageLoading ? 'none' : 'block',
          }}
          draggable={false}
          onLoad={() => {
            setIsImageLoading(false);
          }}
          onClick={handleImageClick}
          onContextMenu={handleContextMenu}
        />
      </div>
    );
  };

  const getDefaultHeader = (): ReactElement => {
    return (
      <div className={styles.lightboxHeader}>
        <Icon icon="icon-arrow-left" size={IconSize.SIZE_ICON_16} type={IconType.DARK_GHOST} shape={IconShape.SQUARE} onClick={handleCloseLightbox} className={styles.backButton} />
      </div>
    );
  };

  if (!currentImage) {
    return null;
  }

  return (
    <Lightbox zIndex={props.zIndex} isOpen={isOpen} onClose={handleCloseLightbox}>
      {headerContent ?? getDefaultHeader()}
      <Swiper onSwipeRight={onPreviousImage} onSwipeLeft={onNextImage} containerClasses={styles.swiperContainer}>
        <div className={styles.imageContentContainer} onClick={handleBackgroundClick} onContextMenu={handleContextMenu}>
          {getPreviousImageIcon()}
          {getZoomableImage()}
          {getNextImageIcon()}
        </div>
      </Swiper>
    </Lightbox>
  );
}
